import React from 'react';
import { t } from './i18n';

interface State {
  error: Error | null;
  reportSent: boolean;
}

interface Props {
  children: React.ReactNode;
}

/**
 * Top-level React error boundary.
 *
 * Catches render-time exceptions, shows a friendly panel, and best-effort
 * forwards a redacted report to /api/client-errors so admins see them in
 * the audit log. Network failure here is silent — we don't want to retry-loop
 * an already-broken page.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, reportSent: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (this.state.reportSent) return;
    this.setState({ reportSent: true });
    const payload = {
      message: error.message?.substring(0, 1024),
      name: error.name,
      stack: (error.stack ?? '').substring(0, 4096),
      componentStack: (info.componentStack ?? '').substring(0, 4096),
      url: typeof window !== 'undefined' ? window.location.pathname : null,
      ts: Math.floor(Date.now() / 1000),
    };
    fetch('/api/client-errors', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {
      /* swallow — never crash the boundary */
    });
  }

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div
        style={{
          maxWidth: 720,
          margin: '60px auto',
          padding: 24,
          background: 'rgba(201, 48, 44, 0.08)',
          border: '1px solid #c9302c',
          borderRadius: 6,
          color: 'inherit',
          fontFamily: 'sans-serif',
        }}
      >
        <h2 style={{ color: '#c9302c', marginTop: 0 }}>{t('errorBoundaryTitle')}</h2>
        <p>{t('errorBoundaryIntro')}</p>
        <div style={{ marginTop: 16 }}>
          <button
            className="primary"
            onClick={() => {
              window.location.reload();
            }}
          >
            {t('errorBoundaryReload')}
          </button>
        </div>
        <details style={{ marginTop: 20 }}>
          <summary style={{ cursor: 'pointer', color: '#666' }}>
            {t('errorBoundaryDetails')}
          </summary>
          <pre
            style={{
              marginTop: 8,
              padding: 12,
              background: 'rgba(127, 127, 127, 0.12)',
              borderRadius: 4,
              fontSize: 11,
              overflow: 'auto',
              maxHeight: 240,
            }}
          >
            {this.state.error.name}: {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
        </details>
      </div>
    );
  }
}
